import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaStar, FaSpinner, FaPen, FaArrowRight } from 'react-icons/fa';
import { SectionHeader, Reveal } from '../../../components/common';
import { fetchApprovedReviews as getApprovedReviews } from '../../../services/reviewService';
import { formatDate } from '../../../utils/formatDate';

const ReviewsSection = () => {
  const [approvedReviews, setApprovedReviews] = useState([]);
  const [isLoadingReviews, setIsLoadingReviews] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadApprovedReviews();
  }, []);

  const loadApprovedReviews = async () => {
    setIsLoadingReviews(true);
    try {
      const reviews = await getApprovedReviews();
      setApprovedReviews(reviews.slice(0, 6));
    } catch (error) {
      console.error('Error fetching approved reviews:', error);
    } finally {
      setIsLoadingReviews(false);
    }
  };

  const getAverageRating = () => {
    if (approvedReviews.length === 0) return 0;
    const sum = approvedReviews.reduce((acc, review) => acc + review.rating, 0);
    return (sum / approvedReviews.length).toFixed(1);
  };

  return (
    <section className="py-16 md:py-24">
      <SectionHeader
        index="06"
        eyebrow="Client reviews"
        title={<>Don't take our word for it. Take theirs.</>}
        lede="Unfiltered feedback from founders and teams we've designed, built and launched with."
        layout="split"
        action={
          approvedReviews.length > 0 ? (
            <div className="flex items-center gap-4">
              <span className="font-display font-extrabold text-5xl text-designer-dark leading-none">
                {getAverageRating()}
              </span>
              <span>
                <span className="flex items-center gap-0.5" aria-label={`${getAverageRating()} out of 5 stars`}>
                  {[...Array(5)].map((_, i) => (
                    <FaStar
                      key={i}
                      className={`w-4 h-4 ${
                        i < Math.round(getAverageRating()) ? 'text-designer-accent' : 'text-gray-200'
                      }`}
                    />
                  ))}
                </span>
                <span className="mt-1 block text-[13px] text-gray-500">
                  {approvedReviews.length} verified {approvedReviews.length === 1 ? 'review' : 'reviews'}
                </span>
              </span>
            </div>
          ) : null
        }
      />

      <div className="mt-10 md:mt-12">
        {isLoadingReviews ? (
          <div className="text-center py-12">
            <FaSpinner className="animate-spin text-4xl text-designer-primary mx-auto mb-4" />
            <p className="text-gray-600">Loading reviews...</p>
          </div>
        ) : approvedReviews.length === 0 ? (
          <div className="text-center py-14 bg-[#FBFAF9] rounded-3xl border border-designer-dark/10">
            <p className="text-gray-500 text-lg mb-5">No reviews yet. Yours could be the first.</p>
            <button
              onClick={() => navigate('/reviews?tab=submit')}
              className="bg-designer-primary text-white px-7 py-3 rounded-full font-semibold text-sm hover:bg-red-700 transition-colors inline-flex items-center gap-2"
            >
              <FaPen className="w-3.5 h-3.5" />
              Be the first to review
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {approvedReviews.map((review, index) => (
                <Reveal key={review.id} delay={(index % 3) * 100} className="h-full">
                  <figure className="h-full flex flex-col bg-white rounded-3xl p-6 md:p-7 border border-designer-dark/10 transition-shadow duration-300 hover:shadow-[0_24px_60px_-28px_rgba(29,32,48,0.35)]">
                    <div className="flex items-center gap-0.5 mb-4" aria-label={`${review.rating} out of 5 stars`}>
                      {[...Array(5)].map((_, i) => (
                        <FaStar
                          key={i}
                          className={`w-3.5 h-3.5 ${
                            i < review.rating ? 'text-designer-accent' : 'text-gray-200'
                          }`}
                        />
                      ))}
                    </div>
                    <blockquote className="text-[15px] text-gray-700 leading-relaxed line-clamp-4 flex-grow">
                      “{review.comment}”
                    </blockquote>
                    <figcaption className="mt-6 pt-5 border-t border-designer-dark/10 flex items-center gap-3">
                      <span className="w-10 h-10 rounded-full bg-designer-dark flex items-center justify-center text-white font-bold text-sm shrink-0" aria-hidden="true">
                        {review.name.charAt(0).toUpperCase()}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-bold text-designer-dark truncate">
                          {review.name}
                        </span>
                        <span className="block text-xs text-gray-400">
                          {formatDate(review.created_at)}
                        </span>
                      </span>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>

            <Reveal className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigate('/reviews?tab=reviews')}
                className="group inline-flex items-center gap-2 bg-designer-dark hover:bg-designer-primary text-white font-semibold text-sm py-3.5 px-7 rounded-full transition-colors"
              >
                View all reviews
                <FaArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
              </button>
              <button
                onClick={() => navigate('/reviews?tab=submit')}
                className="inline-flex items-center gap-2 bg-transparent text-designer-dark border border-designer-dark/20 hover:border-designer-primary hover:text-designer-primary font-semibold text-sm py-3.5 px-7 rounded-full transition-colors"
              >
                <FaPen className="w-3.5 h-3.5" />
                Write a review
              </button>
            </Reveal>
          </>
        )}
      </div>
    </section>
  );
};

export default ReviewsSection;
